import { useCallback, useEffect, useRef, useState } from 'react';

export const SLEEP_TIMER_PRESETS_MINUTES = [5, 10, 15, 20, 25, 30, 45, 60] as const;

export type SleepTimerState =
  | {
      mode: 'countdown';
      minutes: number;
      expiresAt: number;
    }
  | {
      mode: 'end-of-chapter';
    };

interface UseSleepTimerProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  pause: () => void;
  bookId: string;
  onExpire?: (minutes: number) => void;
}

const getRemainingSeconds = (expiresAt: number): number =>
  Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));

export const useSleepTimer = ({ audioRef, pause, bookId, onExpire }: UseSleepTimerProps) => {
  const [sleepTimer, setSleepTimerState] = useState<SleepTimerState | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [isPageVisible, setIsPageVisible] = useState(() =>
    typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const sleepTimerRef = useRef<SleepTimerState | null>(null);
  const previousBookIdRef = useRef(bookId);

  const commitTimerState = useCallback((nextTimer: SleepTimerState | null) => {
    sleepTimerRef.current = nextTimer;
    setSleepTimerState(nextTimer);
    setRemainingSeconds(
      nextTimer?.mode === 'countdown' ? getRemainingSeconds(nextTimer.expiresAt) : null
    );
  }, []);

  const cancelSleepTimer = useCallback(() => {
    commitTimerState(null);
  }, [commitTimerState]);

  const expireCountdown = useCallback(() => {
    const activeTimer = sleepTimerRef.current;
    if (!activeTimer || activeTimer.mode !== 'countdown') {
      return false;
    }

    commitTimerState(null);
    onExpire?.(activeTimer.minutes);
    pause();
    return true;
  }, [commitTimerState, onExpire, pause]);

  const expireCountdownIfDue = useCallback(() => {
    const activeTimer = sleepTimerRef.current;
    if (
      !activeTimer ||
      activeTimer.mode !== 'countdown' ||
      Date.now() < activeTimer.expiresAt
    ) {
      return false;
    }

    return expireCountdown();
  }, [expireCountdown]);

  const setCountdownSleepTimer = useCallback((minutes: number) => {
    commitTimerState({
      mode: 'countdown',
      minutes,
      expiresAt: Date.now() + minutes * 60 * 1000,
    });
  }, [commitTimerState]);

  const setEndOfChapterSleepTimer = useCallback(() => {
    commitTimerState({ mode: 'end-of-chapter' });
  }, [commitTimerState]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsPageVisible(visible);
      if (visible) {
        expireCountdownIfDue();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expireCountdownIfDue]);

  useEffect(() => {
    if (!sleepTimer || sleepTimer.mode !== 'countdown') {
      return;
    }

    const delay = Math.max(0, sleepTimer.expiresAt - Date.now());
    const timeoutId = window.setTimeout(expireCountdownIfDue, delay);
    const audio = audioRef.current;
    const checkExpiry = () => {
      expireCountdownIfDue();
    };

    audio?.addEventListener('timeupdate', checkExpiry);
    audio?.addEventListener('playing', checkExpiry);
    audio?.addEventListener('loadedmetadata', checkExpiry);

    return () => {
      window.clearTimeout(timeoutId);
      audio?.removeEventListener('timeupdate', checkExpiry);
      audio?.removeEventListener('playing', checkExpiry);
      audio?.removeEventListener('loadedmetadata', checkExpiry);
    };
  }, [audioRef, expireCountdownIfDue, sleepTimer]);

  useEffect(() => {
    if (!sleepTimer || sleepTimer.mode !== 'countdown' || !isPageVisible) {
      return;
    }

    const refreshRemainingTime = () => {
      if (expireCountdownIfDue()) {
        return;
      }
      setRemainingSeconds(getRemainingSeconds(sleepTimer.expiresAt));
    };

    refreshRemainingTime();
    const intervalId = window.setInterval(refreshRemainingTime, 1000);
    return () => window.clearInterval(intervalId);
  }, [expireCountdownIfDue, isPageVisible, sleepTimer]);

  useEffect(() => {
    if (previousBookIdRef.current !== bookId) {
      previousBookIdRef.current = bookId;
      cancelSleepTimer();
    }
  }, [bookId, cancelSleepTimer]);

  return {
    sleepTimer,
    remainingSeconds,
    setCountdownSleepTimer,
    setEndOfChapterSleepTimer,
    cancelSleepTimer,
    expireCountdownIfDue,
  };
};
